"use client";

import { useState, useEffect, useRef } from "react";
import PageContent from "@/components/page-content";
import SectionHeader from "@/components/landing/section-header";
import { Workflow, Copy, Check, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Link from "next/link";

interface Point {
  x: number;
  y: number;
}

const exampleData = {
  image: "/images/solutions/identify-defects/cookie_defects.jpeg",
  prompt: "damaged cookies",
  points: [
    { x: 0.250, y: 0.789 },
    { x: 0.373, y: 0.548 },
    { x: 0.674, y: 0.360 },
  ],
  response: `{
  "points": [
    { "x": 0.250, "y": 0.789 },
    { "x": 0.373, "y": 0.548 },
    { "x": 0.674, "y": 0.360 }
  ]
}`,
  responseTimeMs: 738,
  inputTokens: 737,
  outputTokens: 7,
};

const codeExamples = {
  python: `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load your image
image = Image.open("production_line.jpg")

# Point to defects using natural language
result = model.point(image, "damaged cookies")
print(result["points"])`,

  nodejs: `import { vl } from 'moondream';
import fs from 'fs';

// Initialize with API key
const model = new vl({ apiKey: "your-api-key" });

// Load your image
const image = fs.readFileSync("production_line.jpg");

// Point to defects using natural language
const result = await model.point({
  image: image,
  object: "damaged cookies"
});
console.log(result.points);`,

  curl: `curl -X POST https://api.moondream.ai/v1/point \\
  -H 'Content-Type: application/json' \\
  -H 'X-Moondream-Auth: YOUR_API_KEY' \\
  -d '{
    "image_url": "data:image/jpeg;base64,...",
    "object": "damaged cookies"
  }'`,
};

const ImageWithPoints = ({
  image,
  points,
}: {
  image: string;
  points: Point[];
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;

      const MAX_DIMENSION = 400;
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

      // Draw points
      if (points.length > 0) {
        const pointColor = "rgb(34, 197, 94)";
        const transparentColor = "rgba(34, 197, 94, 0.3)";

        points.forEach((point) => {
          const x = point.x * scaledWidth;
          const y = point.y * scaledHeight;
          const outerRadius = 12;
          const middleRadius = 12;
          const innerRadius = 3;

          // Outer stroke
          ctx.beginPath();
          ctx.arc(x, y, outerRadius, 0, 2 * Math.PI);
          ctx.strokeStyle = pointColor;
          ctx.lineWidth = 2;
          ctx.stroke();

          // Middle transparent fill
          ctx.beginPath();
          ctx.arc(x, y, middleRadius, 0, 2 * Math.PI);
          ctx.fillStyle = transparentColor;
          ctx.fill();

          // Inner solid dot
          ctx.beginPath();
          ctx.arc(x, y, innerRadius, 0, 2 * Math.PI);
          ctx.fillStyle = pointColor;
          ctx.fill();
        });
      }
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, points]);

  return <canvas ref={canvasRef} className="rounded-lg border w-full" />;
};

export function HowItWorks() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(key);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const calculateCost = (inputTokens: number, outputTokens: number): string => {
    const inputCost = (inputTokens / 1_000_000) * 0.3;
    const outputCost = (outputTokens / 1_000_000) * 2.5;
    const totalCost = inputCost + outputCost;
    return `$${totalCost.toFixed(6)}`;
  };

  const playgroundLink = `/playground?image=${encodeURIComponent(exampleData.image)}&prompt=${encodeURIComponent(exampleData.prompt)}&skill=point`;

  return (
    <PageContent>
      <div className="space-y-6">
        <SectionHeader
          sectionIcon={<Workflow className="size-4" />}
          section="How it works"
          heading="How point works"
          description="Describe what you're looking for in natural language and get precise (x, y) coordinates instantly."
        />

        <Card className="overflow-hidden">
          <CardContent className="p-6">
            {/* Image + Prompt/Response Layout */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Image with Points and Prompt Overlay */}
              <div className="lg:w-1/2 shrink-0 relative">
                <ImageWithPoints
                  image={exampleData.image}
                  points={exampleData.points}
                />
                {/* Glassmorphism Prompt Overlay */}
                <div className="absolute top-3 left-3 bg-black/30 backdrop-blur-md rounded-lg px-4 py-2 shadow-lg">
                  <p className="text-sm font-mono text-white/90">&quot;{exampleData.prompt}&quot;</p>
                </div>
              </div>

              {/* Response */}
              <div className="lg:w-1/2 flex flex-col gap-4">
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Output
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {exampleData.responseTimeMs}ms •{" "}
                        {exampleData.inputTokens + exampleData.outputTokens} tokens •{" "}
                        {calculateCost(exampleData.inputTokens, exampleData.outputTokens)}
                      </div>
                      <Button asChild size="sm">
                        <Link href={playgroundLink}>
                          Try it
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                  <Card className="bg-muted/50 flex-1">
                    <CardContent className="p-4">
                      <pre className="text-sm whitespace-pre-wrap font-mono">
                        {exampleData.response}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Code Examples */}
            <div className="mt-6 space-y-2">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Code
              </h3>
              <Tabs defaultValue="python" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="python">Python</TabsTrigger>
                  <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                  <TabsTrigger value="curl">cURL</TabsTrigger>
                </TabsList>

                {(["python", "nodejs", "curl"] as const).map((lang) => {
                  const code = codeExamples[lang];
                  const copyKey = `how-it-works-${lang}`;

                  return (
                    <TabsContent key={lang} value={lang} className="mt-4">
                      <div className="relative">
                        <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm border">
                          <code
                            className={`language-${lang === "nodejs" ? "javascript" : lang === "curl" ? "bash" : lang}`}
                          >
                            {code}
                          </code>
                        </pre>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 h-8 w-8 p-0"
                          onClick={() => copyToClipboard(code, copyKey)}
                        >
                          {copiedCode === copyKey ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContent>
  );
}
